
import React, { createContext, useState, useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";

export const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef(null);

  const loadUserFromToken = () => {
    const token = localStorage.getItem("adminToken");

    if (!token) {
      setIsLoggedIn(false);
      setAdmin(null);
      setLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      setAdmin({
        id: decoded.id,
        fname: decoded.fname,
        lname: decoded.lname,
        email: decoded.email,
        phone: decoded.phone,
        user_type: decoded.user_type,
        is_active: decoded.is_active,
        profile_photo: decoded.profile_photo,
        role: decoded.role,
      });
      setIsLoggedIn(true);
      // Keep "role" in sync so axiosInstance interceptor sends the right token.
      // This also repairs existing sessions that logged in before this fix.
      localStorage.setItem("role", "admin");
    } catch (err) {
      console.error("Failed to decode admin token:", err);
      setIsLoggedIn(false);
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserFromToken();
  }, []);

  // Connect socket and fetch notifications when admin logs in
  useEffect(() => {
    if (!admin) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    const token = localStorage.getItem("adminToken");
    if (!token) return;

    // Fetch existing notifications
    fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/notifications`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setNotifications(data.notifications);
          setUnreadCount(data.unreadCount);
        }
      })
      .catch(() => {});

    // Connect socket — loaded on demand so anonymous visitors never pull
    // socket.io-client into the main bundle.
    let cancelled = false;
    import("socket.io-client").then(({ io }) => {
      if (cancelled) return;

      const socket = io(process.env.REACT_APP_SOCKET_URL, {
        auth: { token },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });

      socket.on("notification:new", (notif) => {
        setNotifications((prev) => [notif, ...prev]);
        setUnreadCount((prev) => prev + 1);
      });

      socketRef.current = socket;
    });

    return () => {
      cancelled = true;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [admin]);

  const markAllRead = async () => {
    const token = localStorage.getItem("adminToken");
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/notifications/mark-read`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch (_) {}
  };

  const markOneRead = async (id) => {
    const token = localStorage.getItem("adminToken");
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/notifications/${id}/read`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (_) {}
  };

  const login = (token) => {
    localStorage.setItem("adminToken", token);
    localStorage.setItem("role", "admin");
    loadUserFromToken();
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("role");
    setIsLoggedIn(false);
    setAdmin(null);
    setNotifications([]);
    setUnreadCount(0);
  };

  return (
    <AdminContext.Provider value={{
      isLoggedIn, admin, login, logout, loading,
      notifications, unreadCount, markAllRead, markOneRead,
    }}>
      {children}
    </AdminContext.Provider>
  );
};
export const useAdmin = () => React.useContext(AdminContext);